import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Dialog,
  Field,
  Input,
  MenuButton,
  Select,
  toast,
  useConfirm,
} from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DISPATCH_STATUSES,
  FINANCIAL_STATUSES,
  FULFILLMENT_STATUSES,
  useApi,
  type OrderDTO,
  type ProductionJobCoreDTO,
  type ProductionJobDTO,
} from "../../api";
import {
  DISPATCH_VARIANT,
  FINANCIAL_VARIANT,
  FULFILLMENT_VARIANT,
  ORDER_KIND_VARIANT,
} from "../../components/badges";
import { ExternalLink, formatDateTime, money, TwoLine } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { ImportFileDialog } from "../artwork/dialogs";
import { LogicView } from "../../views";
import { DispatchDialog } from "./DispatchDialog";
import { NewDraftOrderDialog } from "./NewDraftOrderDialog";
import {
  clientId as rowClientId,
  customer as rowCustomer,
  dispatchStatus,
  financialStatus,
  fulfillmentStatus,
  headerId,
  itemCount,
  orderDate,
  orderGid,
  orderIdStripped,
  orderKind,
  orderName,
  shippingAddress,
  storeId as rowStoreId,
  toRow,
  total as rowTotal,
  type OrderRow,
} from "./orderData";

/**
 * Orders and draft orders in one queue.
 *
 * The list covers this calendar year plus every open draft — the same window
 * `OrderView` loaded, which is what keeps a Shopify-backed grid usable without
 * paging. The dispatch pill is the working control: click it on an order that
 * still owes production and the dispatch dialog opens on its outstanding lines.
 */
export function OrdersPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.ORDERS);
  const title = t("navigation.item.orders", "Orders");

  const [selected, setSelected] = useState<OrderRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importClientId, setImportClientId] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [dispatchFor, setDispatchFor] = useState<OrderDTO | null>(null);
  const [dispatchJobs, setDispatchJobs] = useState<ProductionJobDTO[]>([]);

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: allowed,
  });

  const clients = useQuery({
    queryKey: ["client"],
    queryFn: () => api.clients.findAll(),
    enabled: allowed,
  });

  const drafts = useQuery({
    queryKey: ["draft-order"],
    queryFn: () => api.draftOrders.findAll(),
    enabled: allowed,
  });

  const orders = useQuery({
    queryKey: ["order", "year"],
    queryFn: () => {
      const from = new Date(new Date().getFullYear(), 0, 1).toISOString();
      return api.orders.getByDateRange(from, new Date().toISOString());
    },
    enabled: allowed,
  });

  /** Production jobs keyed by the *Shopify order row id* they belong to. */
  const jobs = useQuery({
    queryKey: ["production", "job", "core"],
    queryFn: () => api.productionJobCores.findAll(),
    enabled: allowed,
  });

  const jobsByOrder = useMemo(() => {
    const map = new Map<number, ProductionJobCoreDTO[]>();
    for (const job of jobs.data ?? []) {
      const list = map.get(job.order_id) ?? [];
      list.push(job);
      map.set(job.order_id, list);
    }
    return map;
  }, [jobs.data]);

  const storeById = useMemo(
    () => new Map((stores.data ?? []).map((entry) => [entry.id, entry])),
    [stores.data],
  );
  const clientById = useMemo(
    () => new Map((clients.data ?? []).map((entry) => [entry.id, entry])),
    [clients.data],
  );

  const rows = useMemo<OrderRow[]>(
    () => [
      ...(drafts.data ?? []).map((entry) => toRow(entry, true)),
      ...(orders.data ?? []).map((entry) => toRow(entry, false)),
    ],
    [drafts.data, orders.data],
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["draft-order"] });
    await queryClient.invalidateQueries({ queryKey: ["order", "year"] });
    await queryClient.invalidateQueries({ queryKey: ["production", "job", "core"] });
  };

  const openDispatch = async (row: OrderRow) => {
    if (row.isDraft || !row.order) return;
    const ids = (jobsByOrder.get(row.order.id) ?? []).map((job) => job.id);
    const full = ids.length > 0 ? await api.productionJobs.findByIds(ids) : [];
    setDispatchJobs(full);
    setDispatchFor(row.order);
  };

  const cols = useMemo(() => {
    const c = defineColumns<OrderRow>();
    return [
      c.text("store", (row) => storeById.get(rowStoreId(row))?.name ?? "", {
        headerKey: "column.store",
        filter: "select",
        priority: 3,
      }),
      c.custom(
        "client",
        (row) => {
          const id = rowClientId(row);
          return id ? (clientById.get(id)?.name ?? "") : "";
        },
        (row) => {
          const id = rowClientId(row);
          const client = id ? clientById.get(id) : undefined;
          return client ? (
            <TwoLine primary={client.name} secondary={client.code} />
          ) : (
            <span className="text-ink-faint">no client</span>
          );
        },
        { headerKey: "column.client", filter: "select", priority: 2 },
      ),
      c.custom(
        "order",
        (row) => orderDate(row) ?? "",
        (row) => {
          const store = storeById.get(rowStoreId(row));
          const href = store
            ? `${store.url}${row.isDraft ? "draft_orders/" : "orders/"}${orderIdStripped(row)}`
            : "";
          return (
            <span className="flex flex-col leading-tight">
              {href ? (
                <ExternalLink href={href}>{orderName(row)}</ExternalLink>
              ) : (
                <span className="font-medium">{orderName(row)}</span>
              )}
              <span className="text-[11px] text-ink-muted">
                {formatDateTime(orderDate(row)) || "invalid date"}
              </span>
            </span>
          );
        },
        { headerKey: "column.order", priority: 1 },
      ),
      c.custom(
        "kind",
        (row) => orderKind(row),
        (row) => <Badge variant={ORDER_KIND_VARIANT[orderKind(row)]}>{orderKind(row)}</Badge>,
        { headerKey: "column.status", filter: "select", priority: 2 },
      ),
      c.custom(
        "fulfillment",
        (row) => fulfillmentStatus(row),
        (row) => {
          const status = fulfillmentStatus(row);
          return <Badge variant={FULFILLMENT_VARIANT[status] ?? "neutral"}>{status}</Badge>;
        },
        {
          headerKey: "column.fulfillment-status",
          filter: "select",
          filterOptions: FULFILLMENT_STATUSES.map((value) => ({ value, label: value })),
          priority: 3,
        },
      ),
      c.custom(
        "financial",
        (row) => financialStatus(row),
        (row) => {
          const status = financialStatus(row);
          return <Badge variant={FINANCIAL_VARIANT[status] ?? "neutral"}>{status}</Badge>;
        },
        {
          headerKey: "column.financial-status",
          filter: "select",
          filterOptions: FINANCIAL_STATUSES.map((value) => ({ value, label: value })),
          priority: 3,
        },
      ),
      c.custom(
        "dispatch",
        (row) => dispatchStatus(row, row.order ? jobsByOrder.get(row.order.id) : undefined),
        (row) => {
          const status = dispatchStatus(
            row,
            row.order ? jobsByOrder.get(row.order.id) : undefined,
          );
          const actionable =
            !row.isDraft && (status === "NOT_DISPATCHED" || status === "PARTIAL_DISPATCH");
          const badge = <Badge variant={DISPATCH_VARIANT[status]}>{status}</Badge>;
          return actionable ? (
            <button
              type="button"
              className="cursor-pointer"
              onClick={(event) => {
                event.stopPropagation();
                void openDispatch(row);
              }}
            >
              {badge}
            </button>
          ) : (
            badge
          );
        },
        {
          headerKey: "column.dispatch-status",
          filter: "select",
          filterOptions: DISPATCH_STATUSES.map((value) => ({ value, label: value })),
          priority: 2,
        },
      ),
      c.custom(
        "customer",
        (row) => rowCustomer(row)?.displayName ?? "",
        (row) => {
          const person = rowCustomer(row);
          const address = shippingAddress(row);
          return (
            <TwoLine
              primary={person?.displayName ?? "no customer data"}
              secondary={
                address
                  ? [address.address1, address.city, address.provinceCode, address.countryCodeV2]
                      .filter(Boolean)
                      .join(", ")
                  : "no address data"
              }
            />
          );
        },
        { headerKey: "column.customer", priority: 2 },
      ),
      c.number("items", (row) => itemCount(row), { headerKey: "column.items", priority: 4 }),
      c.custom(
        "total",
        (row) => Number(rowTotal(row)?.shopMoney.amount ?? 0),
        (row) => {
          const bag = rowTotal(row);
          return (
            <TwoLine
              primary={bag ? `$${money(bag.shopMoney.amount)}` : "—"}
              secondary={bag?.shopMoney.currencyCode}
            />
          );
        },
        { headerKey: "column.total", filter: "number", align: "right", priority: 2 },
      ),
    ];
  }, [clientById, jobsByOrder, storeById]);

  const draftSelection = selected.filter((row) => row.isDraft);

  const completeDrafts = async () => {
    const ok = await confirm({
      title: t("dialog.confirm.complete-draft-orders.title", "Complete draft orders"),
      message: t(
        "dialog.confirm.complete-draft-orders.text",
        "Are you sure you want to complete the draft orders?",
      ),
    });
    if (!ok) return;
    for (const row of draftSelection) {
      try {
        await api.shopifyOrders.draftComplete(rowStoreId(row), orderGid(row));
      } catch (failure) {
        toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
      }
    }
    setSelected([]);
    await refresh();
  };

  const deleteDrafts = async () => {
    const ok = await confirm({
      title: t("dialog.confirm.delete-draft-orders.title", "Delete draft orders"),
      message: t(
        "dialog.confirm.delete-draft-orders.text",
        "Are you sure you want to delete the draft orders?",
      ),
      variant: "danger",
    });
    if (!ok) return;
    for (const row of draftSelection) {
      try {
        await api.shopifyOrders.draftDelete(rowStoreId(row), orderGid(row));
      } catch (failure) {
        toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
      }
    }
    setSelected([]);
    await refresh();
  };

  const defaultStore = (stores.data ?? []).find((entry) => entry.is_default) ?? stores.data?.[0];

  if (!allowed) return <AccessDenied title={title} />;
  if (drafts.error || orders.error)
    return <ErrorState title={title} error={drafts.error ?? orders.error} />;

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <MenuButton
            label={t("menu.item.root.options", "Options")}
            items={[
              {
                label: t("menu.item.new-order", "New order"),
                onClick: () => setCreating(true),
              },
              {
                label: t("menu.item.complete-draft-orders", "Complete draft order"),
                disabled: draftSelection.length === 0,
                onClick: () => void completeDrafts(),
              },
              {
                label: t("menu.item.delete-draft-orders", "Delete draft order"),
                disabled: draftSelection.length === 0,
                onClick: () => void deleteDrafts(),
              },
              {
                label: t("menu.item.import-orders", "Import orders"),
                onClick: () => setImporting(true),
              },
              {
                label: t("menu.item.sync-orders", "Sync orders"),
                onClick: () => setSyncing(true),
              },
            ]}
          />
        }
      />

      <DataTable<OrderRow>
        data={rows}
        columns={cols}
        getRowId={(row) => row.key}
        stateKey="/orders"
        loading={drafts.isLoading || orders.isLoading}
        selectable
        onSelectionChange={setSelected}
        initialSorting={[{ id: "order", desc: true }]}
        onRowClick={(row) =>
          void navigate({ to: "/orders/$id", params: { id: String(headerId(row)) } })
        }
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("tab.production-job", "Production job")}
            icon={<Eye className="size-3.5" />}
            onClick={() =>
              void navigate({ to: "/orders/$id", params: { id: String(headerId(row)) } })
            }
          />
        )}
      />

      <NewDraftOrderDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => void refresh()}
      />

      <ImportFileDialog
        open={importing}
        onClose={() => setImporting(false)}
        title={t("dialog.import-orders.title", "Import orders")}
        accept=".xlsx,.xls,.csv"
        disabled={!importClientId || !defaultStore}
        extra={
          <Field label={t("form.field.client", "Client")} required>
            <Select
              value={importClientId}
              options={(clients.data ?? []).map((entry) => ({
                value: String(entry.id),
                label: `${entry.name} - ${entry.code}`,
              }))}
              placeholder={t("form.field.select", "Select")}
              onChange={(event) => setImportClientId(event.target.value)}
            />
          </Field>
        }
        onImport={(file) =>
          api.shopifyOrders.importOrders(defaultStore!.id, Number(importClientId), file)
        }
        onImported={() => void refresh()}
      />

      <SyncOrdersDialog open={syncing} onClose={() => setSyncing(false)} />

      <DispatchDialog
        open={dispatchFor !== null}
        onClose={() => setDispatchFor(null)}
        order={dispatchFor}
        existingJobs={dispatchJobs}
        onDispatched={() => void refresh()}
      />
    </>
  );
}

/**
 * Pulls orders and draft orders from a storefront.
 *
 * "Since last sync" is the everyday path; the from-a-date mode exists for
 * backfills after an outage, and both run asynchronously server-side — the
 * grid catches up on its next load rather than blocking on the import.
 */
function SyncOrdersDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [storeId, setStoreId] = useState("");
  const [fromDate, setFromDate] = useState(false);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: open,
  });

  const submit = async () => {
    if (!storeId) return;
    setBusy(true);
    try {
      const iso = fromDate && date ? new Date(`${date}T00:00:00Z`).toISOString() : undefined;
      await api.shopifyOrders.syncAsync(Number(storeId), iso);
      toast.success(t("notification.sync.orders.success", "Order sync started"));
      await api.shopifyOrders.draftSyncAsync(Number(storeId));
      toast.success(t("notification.sync.draft-orders.success", "Draft order sync started"));
      onClose();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.sync.orders.error", "Failed to start order sync"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.sync.orders.title", "Sync orders")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!storeId || (fromDate && !date)}
            onClick={() => void submit()}
          >
            {t("dialog.confirm.sync", "Sync")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("form.field.store", "Store")} required>
          <Select
            value={storeId}
            options={(stores.data ?? []).map((entry) => ({
              value: String(entry.id),
              label: entry.name,
            }))}
            placeholder={t("form.field.select", "Select")}
            onChange={(event) => setStoreId(event.target.value)}
          />
        </Field>

        <Field label={t("form.field.sync.orders.mode", "Sync mode")}>
          <div className="flex flex-col gap-1.5 text-[13px] text-ink">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={!fromDate}
                onChange={() => setFromDate(false)}
                className="accent-[var(--accent)]"
              />
              {t("form.field.sync.orders.mode.last-sync", "Since last sync")}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={fromDate}
                onChange={() => setFromDate(true)}
                className="accent-[var(--accent)]"
              />
              {t("form.field.sync.orders.mode.from-date", "From a date")}
            </label>
          </div>
        </Field>

        {fromDate && (
          <Field label={t("form.field.sync.orders.date", "From date")} required>
            <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
